import { Button, ConfirmModal, DrawerFooter } from "@/components";
import {
  FormDatePicker,
  FormDropzoneMultiple,
  FormInput,
  FormRadioGroup,
  FormSelectMultiple,
} from "@/components/Form";
import { DROPZONE_ACCEPT } from "@/constants/constants";
import { ROUTES } from "@/constants/routes";
import { createNotification } from "@/services";
import { useCompanyStore } from "@/store/companyStore";
import { useNotificationStore } from "@/store/notificationStore";
import {
  NewNotificationParams,
  Notification,
  NOTIFICATION_QUERY_KEYS,
  NotificationStatus,
  RoleEnum,
  SendToAllEnum,
} from "@/types";
import {
  formatDate,
  jsonToFormData,
  toastApiError,
  toastSuccess,
} from "@/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalError } from "../components";
import { useFormNotification } from "../hooks";
import {
  ENotificationTab,
  KEYS_NOTIFICATION_SEARCH_PARAMS,
} from "../Notifications";
import { NewNotificationSchemaType } from "../schemas";

type Props = {
  onClose: () => void;
  initialData?: Notification;
  role: RoleEnum; // check role to display form correctly
};

const FormNotification: React.FC<Props> = ({ onClose, initialData, role }) => {
  const [open, setOpen] = useState(false);
  const { reset } = useNotificationStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Reset form when modal is closed
  useEffect(() => {
    return reset;
  }, []);

  const closeAll = () => {
    setOpen(false);
    onClose();
  };

  const {
    control,
    handleSubmit,
    timeSendNotification,
    options,
    updateSendTimeIfPast,
    isDisplaySelectGroups,
    isDisplaySelectStores,
    isDisplaySelectUsers,
    // createFileFromUrls,
    // search option input
    setSearchGroups,
    setSearchStores,
    setSearchUsers,
    // option groups
    storeGroups,
    fetchNextPageGroups,
    hasNextPageGroups,
    isFetchingGroups,
    // option stores
    myCompanyStores,
    fetchNextPageStores,
    hasNextPageStores,
    isFetchingStores,
    // option users
    storeUsers,
    fetchNextPageUsers,
    hasNextPageUsers,
    isFetchingUsers,
  } = useFormNotification(role, initialData);

  const { company, fetchMyCompany } = useCompanyStore();

  useEffect(() => {
    fetchMyCompany();
  }, []);

  const storeDisplayName = useMemo(() => {
    console.log(company);

    return company?.store_display_name || "店舗";
  }, [company]);

  const resetApiList = () => {
    queryClient.invalidateQueries({
      queryKey: [NOTIFICATION_QUERY_KEYS.ALL_NOTIFICATIONS],
    });
  };

  // Handle create notification success
  const handleCreateSuccess = (data: any) => {
    toastSuccess(data?.message || "Success");
    resetApiList();
    closeAll();
  };

  // Process data and call API
  const parseDataToSubmitByRole = useCallback(
    async (
      data: NewNotificationSchemaType,
      role: RoleEnum,
    ): Promise<NewNotificationParams> => {
      // Nếu trong mảng fileItems có item chứa url thì tạo file từ url đó rồi mới submit
      // copy function
      // const files = await createFileFromUrls(data.fileItems || []);

      // Copy functionality, if file url exists, just pass file url to server
      const files =
        data.fileItems
          ?.filter((item) => typeof item.file === "object")
          .map((item) => item.file) || [];
      const file_urls =
        data.fileItems?.filter((item) => item.url).map((item) => item.url) ||
        [];

      const newData: NewNotificationParams = {
        send_time: data.send_time,
        title: data.title,
        description: data.description,
        manager: data.manager,
        status: data.status,
        files,
        file_urls,
      };

      // [SENDER by ROLE]
      // Role case: company admin & head office user
      // Send to: all store groups & all stores (only myself)
      if (
        role === RoleEnum.COMPANY_ADMIN ||
        role === RoleEnum.HEAD_OFFICE_USER
      ) {
        // [SENDER STORES]
        if (data.send_to_all_stores === SendToAllEnum.ALL) {
          newData.send_to_all_stores = 1;
          newData.storeIds = [];
        } else if (data.send_to_all_stores === SendToAllEnum.NONE) {
          newData.send_to_all_stores = 0;
          newData.storeIds = [];
        } else {
          newData.send_to_all_stores = 0;
          newData.storeIds = data.stores?.map((item) => item.value) || []; // get key from option
        }

        // [SENDER GROUPS]
        if (data.send_to_all_groups === SendToAllEnum.ALL) {
          newData.send_to_all_groups = 1;
          newData.groupIds = [];
        } else if (data.send_to_all_groups === SendToAllEnum.NONE) {
          newData.send_to_all_groups = 0;
          newData.groupIds = [];
        } else {
          newData.send_to_all_groups = 0;
          newData.groupIds = data.groups?.map((group) => group.value) || []; // get key from option
        }
      }

      // Role case 2: Store manager
      // Send to: all members in my store
      if (role === RoleEnum.STORE_MANAGER) {
        // [SENDER USERS]
        if (data.send_to_all_users === SendToAllEnum.ALL) {
          newData.send_to_all_users = 1;
          newData.storeUserIds = [];
        } else if (data.send_to_all_users === SendToAllEnum.NONE) {
          newData.send_to_all_users = 0;
          newData.storeUserIds = [];
        } else {
          newData.send_to_all_users = 0;
          newData.storeUserIds =
            data.storeUsers?.map((user) => user.value) || []; // get key from option
        }
      }

      return newData;
    },
    [],
  );

  // Handle create notification
  const handleCreateNotification = useCallback(
    async ({
      data,
      role,
    }: {
      data: NewNotificationSchemaType;
      role: RoleEnum;
    }) => {
      const newData = await parseDataToSubmitByRole(data, role);
      const formData = jsonToFormData(newData);
      return createNotification(formData);
    },
    [],
  );

  const minDate = useMemo(() => {
    return new Date(new Date().getTime() + 60 * 60 * 1000);
  }, []);

  // Create notification with ready status
  const { mutateAsync: mutateCreate, isPending: isPendingCreate } = useMutation(
    {
      mutationKey: [NOTIFICATION_QUERY_KEYS.CREATE],
      mutationFn: handleCreateNotification,
      onSuccess: handleCreateSuccess,
      onError: toastApiError,
    },
  );

  // Create notification with draft status
  const { mutateAsync: mutateDraft, isPending: isPendingDraft } = useMutation({
    mutationKey: [NOTIFICATION_QUERY_KEYS.CREATE_DRAFT],
    mutationFn: handleCreateNotification,
    onSuccess: handleCreateSuccess,
    onError: toastApiError,
  });

  const backTab1 = () => {
    navigate(
      ROUTES.NOTIFICATION_MANAGEMENT +
        `?${KEYS_NOTIFICATION_SEARCH_PARAMS.TAB}=${ENotificationTab.NOTIFICATION_MANAGEMENT}`,
    );
  };

  // handle button create
  const handleCreate = () => {
    updateSendTimeIfPast();
    // validate form, if valid open confirm modal
    handleSubmit(() => {
      setOpen(true);
    })();
  };

  // create draft
  const createDraft = () => {
    updateSendTimeIfPast();
    handleSubmit((data) => {
      data.status = NotificationStatus.DRAFT;
      mutateDraft({ data, role }).then(backTab1);
    })();
  };

  // create
  const create = () => {
    handleSubmit((data) => {
      data.status = NotificationStatus.READY;
      mutateCreate({ data, role }).then(backTab1);
    })();
  };

  // handle form submit
  const handleFormSubmit = () => {
    handleCreate();
  };

  return (
    <div className="flex flex-col h-full">
      <form
        id="new-notification"
        className="space-y-4 flex-1 p-4 overflow-y-auto"
        onSubmit={handleFormSubmit}
      >
        <FormDatePicker
          control={control}
          name="send_time"
          label="通知日"
          placeholder="YYYY/MM/DD 00:00"
          required
          showTimeSelect
          dateFormat="yyyy/MM/dd HH:mm"
          minDate={minDate} // + 1h
        />
        <FormInput
          control={control}
          name="title"
          label="通知テーマ"
          placeholder="通知テーマを入力してください。"
          required
          maxLength={150}
        />
        <FormInput
          control={control}
          name="description"
          label="通知内容"
          placeholder="通知内容を入力してください。"
          multiple
          multiline
          maxLength={2000}
          required
        />

        <FormInput
          control={control}
          name="manager"
          label="通知者"
          required
          maxLength={150}
        />

        {/* Company admin can select group, store to send notification */}
        {role === RoleEnum.COMPANY_ADMIN && (
          <>
            <div className="font-bold leading-6">グループ</div>

            <FormRadioGroup
              options={options}
              control={control}
              name="send_to_all_groups"
            />

            {isDisplaySelectGroups && (
              <FormSelectMultiple
                options={storeGroups}
                allowSearchInput
                control={control}
                name="groups"
                onLoadMoreItems={fetchNextPageGroups}
                onSearchInputChange={setSearchGroups}
                label="グループ"
                placeholder="グループを選択してください"
                isLoadingMoreItems={isFetchingGroups}
                stopCheckScrollToBottom={!hasNextPageGroups}
              />
            )}

            <div className="font-bold leading-6">{storeDisplayName}</div>

            <FormRadioGroup
              options={options}
              control={control}
              name="send_to_all_stores"
            />

            {isDisplaySelectStores && (
              <FormSelectMultiple
                options={myCompanyStores}
                allowSearchInput
                control={control}
                name="stores"
                onLoadMoreItems={fetchNextPageStores}
                onSearchInputChange={setSearchStores}
                label={storeDisplayName}
                placeholder={`${storeDisplayName}を選択してください`}
                isLoadingMoreItems={isFetchingStores}
                stopCheckScrollToBottom={!hasNextPageStores}
              />
            )}
          </>
        )}

        {/* Head office user can select store to send notification */}
        {role === RoleEnum.HEAD_OFFICE_USER && (
          <>
            <div className="font-bold leading-6">{storeDisplayName}</div>

            <FormRadioGroup
              options={options}
              control={control}
              name="send_to_all_stores"
            />

            {isDisplaySelectStores && (
              <FormSelectMultiple
                options={myCompanyStores}
                allowSearchInput
                control={control}
                name="stores"
                onLoadMoreItems={fetchNextPageStores}
                onSearchInputChange={setSearchStores}
                label={storeDisplayName}
                placeholder={`${storeDisplayName}を選択してください`}
                isLoadingMoreItems={isFetchingStores}
                stopCheckScrollToBottom={!hasNextPageStores}
              />
            )}
          </>
        )}

        {/* Store manager can select user to send notification  */}
        {role === RoleEnum.STORE_MANAGER && (
          <>
            <div className="font-bold leading-6">店員</div>

            <FormRadioGroup
              options={options}
              control={control}
              name="send_to_all_users"
            />

            {isDisplaySelectUsers && (
              <FormSelectMultiple
                options={storeUsers}
                allowSearchInput
                control={control}
                name="storeUsers"
                onLoadMoreItems={fetchNextPageUsers}
                onSearchInputChange={setSearchUsers}
                label="店員"
                placeholder="店員を選択してください"
                isLoadingMoreItems={isFetchingUsers}
                stopCheckScrollToBottom={!hasNextPageUsers}
                enableOptionDescription
              />
            )}
          </>
        )}

        <GlobalError control={control} />

        <FormDropzoneMultiple
          control={control}
          name="fileItems"
          nameDelete="fileDeletedIds"
          accept={{
            ...DROPZONE_ACCEPT.IMAGE.COMMON,
            ...DROPZONE_ACCEPT.PDF,
            ...DROPZONE_ACCEPT.VIDEO.COMMON,
          }}
          maxSizeInBytes={500 * 1024 * 1024} // 500MB
          errorFileSize="最大アップロードサイズを超過しています。サイズの上限は500MBです。"
          errorInvalidType="画像は MP4, PDF, PNG, JPG, JPGE のうちいずれかの形式のファイルを指定してください。"
          maxFiles={5}
        />
      </form>

      <DrawerFooter>
        <Button
          type="button"
          buttonType="text"
          color="default"
          onClick={onClose}
          className="!h-12"
        >
          キャンセル
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="default"
          onClick={createDraft}
          className="!h-12"
          isLoading={isPendingDraft}
        >
          下書き保存
        </Button>
        <Button
          type="button"
          variant="solid"
          color="primary"
          className="!h-12"
          onClick={handleCreate}
        >
          通知
        </Button>
      </DrawerFooter>

      <ConfirmModal
        type="create"
        open={open}
        onOpenChange={setOpen}
        onConfirm={create}
        title="確認"
        confirmation={
          <p className="text-sm leading-[1.375rem] px-4 py-2 -mx-4">
            {timeSendNotification && formatDate(timeSendNotification)}
            となったら自動通知されます。
            <br />
            よろしいでしょうか。
          </p>
        }
        isPending={isPendingCreate}
      />
    </div>
  );
};

export default FormNotification;
